import { isNil } from 'ramda';
import {
  of, concat, Observable, EMPTY,
} from 'rxjs';

import { ERRORS } from '@ezugi/constants';
import { GenericPayloadType } from '@ezugi/types';

import { ClientError, HTTPError } from './errors';

import type { ActionCreatorWithPayload, ActionCreatorWithoutPayload } from '@reduxjs/toolkit';

const { CODES, GAME_LOAD_FAILURE } = ERRORS;

type ParamOptions = Partial<{
  payloadKey: string;
  httpErrorHandler: (error: HTTPError) => unknown;
  clientErrorHandler: (error: ClientError) => unknown;
}>;

export type ErrorParams = {
  error: HTTPError | ClientError;
  failAction: ActionCreatorWithPayload<GenericPayloadType> | ActionCreatorWithoutPayload;
  options?: ParamOptions;
}

export function handleError({
  error,
  failAction,
  options = {},
}: ErrorParams) {
  if (error instanceof HTTPError) {
    return handleHttpError({ error, failAction, options });
  }

  return handleClientError({ error, failAction, options });
}

type HttpErrorParams = {
  error: HTTPError;
  failAction: ActionCreatorWithPayload<any> | ActionCreatorWithoutPayload;
  options: ParamOptions;
}

export function handleHttpError({
  error,
  failAction,
  options: {
    payloadKey,
    httpErrorHandler,
  },
}: HttpErrorParams) {
  const handlerResult = httpErrorHandler ? httpErrorHandler(error) : null;
  const { response: { errorCode } } = error;
  const errorData = { code: errorCode };
  const payload: GenericPayloadType = payloadKey
    ? { [payloadKey]: errorData } : { ...errorData };
  return concat(
    of(failAction(payload)),
    handlerResult instanceof Observable ? handlerResult : EMPTY
  );
}

type ClientErrorParams = {
  error: ClientError;
  failAction: ActionCreatorWithPayload<any> | ActionCreatorWithoutPayload;
  options: ParamOptions;
}

export function handleClientError({
  error,
  failAction,
  options: {
    payloadKey,
    clientErrorHandler,
  },
}: ClientErrorParams) {
  const handlerResult = clientErrorHandler ? clientErrorHandler(error) : null;
  const errorData = {
    code: CODES[GAME_LOAD_FAILURE],
    message: error?.message,
    title: 'errors.game_load_failure_title',
    description: 'errors.game_load_failure_desc',
    actionLabel: 'general.go_back',
  };

  const payload: GenericPayloadType = payloadKey
    ? { [payloadKey]: errorData } : { ...errorData };

  return concat(
    isNil(failAction) ? EMPTY : of(failAction(payload)),
    handlerResult instanceof Observable ? handlerResult : EMPTY
  );
}
